import React, { useState } from 'react';
import { FiDollarSign, FiClock, FiCheckCircle, FiXCircle, FiCalendar, FiBox } from 'react-icons/fi';
import { useTheme } from '../../context/ThemeContext';
import RetryPaymentModal from './RetryPaymentModal';

const FinancialPanel = ({ totalInvested, formatPrice, userApps }) => {
  const { isDarkMode } = useTheme();
  const [selectedAppForPayment, setSelectedAppForPayment] = useState(null);
  const [dynamicPrice, setDynamicPrice] = useState(119.90);

  React.useEffect(() => {
    if (userApps.some(app => !Number(app.totalPrice))) {
      import('../../firebase').then(({ db }) => {
        import('firebase/firestore').then(({ doc, getDoc }) => {
          const docRef = doc(db, 'settings', 'pricing');
          getDoc(docRef).then(docSnap => {
            if (docSnap.exists()) {
              const basePrice = docSnap.data().base;
              setDynamicPrice(basePrice !== undefined && basePrice !== null ? basePrice : 119.90);
            }
          }).catch(err => console.log('Erro ao buscar pricing no painel financeiro:', err));
        });
      });
    }
  }, [userApps]);

  const stats = [
    {
      title: 'Total Investido',
      value: formatPrice(totalInvested),
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      textColor: 'text-blue-900 dark:text-blue-200',
      accentColor: 'text-blue-800 dark:text-blue-300',
      iconBgColor: 'bg-blue-100 dark:bg-gray-700 dark:text-white',
      icon: <FiDollarSign className="w-6 h-6" />
    },
    {
      title: 'Total de Apps',
      value: userApps.length,
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      textColor: 'text-green-900 dark:text-green-200',
      accentColor: 'text-green-800 dark:text-green-300',
      iconBgColor: 'bg-green-100 dark:bg-gray-700 dark:text-white',
      icon: <FiBox className="w-6 h-6" />
    }
  ];

  const getPaymentStatusIcon = (status) => {
    if (!status) return <FiClock className="w-4 h-4" />;
    const s = status.toLowerCase();
    
    if (s === 'paid') return <FiCheckCircle className="w-4 h-4" />;
    if (s === 'defaulting') return <FiXCircle className="w-4 h-4" />;
    
    if (s.includes('approve') || s === 'active' || s === 'development' || s === 'published') {
      return <FiCheckCircle className="w-4 h-4" />;
    } else if (s.includes('reject') || s === 'cancelled') {
      return <FiXCircle className="w-4 h-4" />;
    } else {
      return <FiClock className="w-4 h-4" />;
    }
  };

  const getPaymentStatusColor = (status) => {
    if (!status) return isDarkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
    const s = status.toLowerCase();
    
    if (s === 'paid') return isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800';
    if (s === 'defaulting') return isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-800';
    
    if (s.includes('approve') || s === 'active' || s === 'development' || s === 'published') {
      return isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800';
    } else if (s.includes('reject') || s === 'cancelled') {
      return isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-800';
    } else {
      return isDarkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
    }
  };

  const getPaymentStatusText = (status) => {
    if (!status) return 'Pendente';
    const s = status.toLowerCase();
    
    if (s === 'paid') return 'Pago';
    if (s === 'defaulting') return 'Inadimplente';
    
    if (s.includes('approve') || s === 'active' || s === 'development' || s === 'published') {
      return 'Aprovado';
    } else if (s.includes('reject') || s === 'cancelled') {
      return 'Recusado/Cancelado';
    } else if (s.includes('pending') || s === 'inactive') {
      return 'Aguardando Pagamento';
    } else if (s.includes('in_process')) {
      return 'Em Processamento';
    }
    return 'Pendente';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    // Firestore timestamp
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString('pt-BR');
    }
    // Already date object or string
    const d = new Date(timestamp);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('pt-BR');
    }
    return 'N/A';
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border-gray-100 dark:bg-gray-800 dark:border-gray-700 p-6 rounded-none shadow-sm border hover:shadow-md transition-shadow duration-300">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white">Resumo Financeiro</h3>
          <div className="w-10 h-10 bg-gradient-to-r from-blue-100 to-green-100 dark:bg-gray-700 dark:bg-none rounded-none flex items-center justify-center">
            <FiDollarSign className="w-5 h-5 text-gray-600 dark:text-white" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`${stat.bgColor} border-opacity-20 dark:border-gray-600 p-5 rounded-none border transition-transform duration-200 hover:scale-[1.02]`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className={`text-sm font-medium ${stat.accentColor}`}>
                  {stat.title}
                </span>
                <div className={`p-2 rounded-none ${stat.iconBgColor}`}>
                  {stat.icon}
                </div>
              </div>
              
              <p className={`text-2xl font-bold ${stat.textColor}`}>
                {stat.value}
              </p>
              
              <div className="mt-3 pt-3 border-t border-current border-opacity-20 dark:border-gray-600">
                <span className={`text-xs ${stat.accentColor} opacity-70 dark:opacity-90`}>
                  {index === 0 ? 'Soma total de todos os aplicativos ativos' : 'Aplicativos cadastrados e em processo'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {userApps.length > 0 && (
          <div className="mt-6 pt-4 border-gray-100 dark:border-gray-700 border-t">
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
              <span>Média por app</span>
              <span className="font-medium text-gray-800 dark:text-white">
                {formatPrice(totalInvested / userApps.length)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Histórico de Faturas e Pagamentos */}
      <div className="bg-white border-gray-100 dark:bg-gray-800 dark:border-gray-700 p-6 rounded-none shadow-sm border hover:shadow-md transition-shadow duration-300">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white flex items-center gap-2">
            <FiCalendar className="w-5 h-5" /> Faturas e Pagamentos
          </h3>
        </div>

        {userApps.length > 0 ? (
          <div className="overflow-x-auto w-full custom-scrollbar">
            <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-700">
              <thead className="bg-gray-50/50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Serviço / App</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Data</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Valor</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status do Pagamento</th>
                  <th className="px-4 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Ação</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-50 dark:divide-gray-700/50">
                {userApps.map(app => {
                  // Use either paymentStatus or general status to infer
                  const statusRaw = app.paymentStatus || app.status;
                  const isPending = getPaymentStatusText(statusRaw) === 'Aguardando Pagamento';

                  return (
                    <tr key={app.id} className={`${isDarkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} transition-colors duration-200`}>
                      <td className="px-4 py-4 align-middle">
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">Assinatura CriaApp</span>
                          <span className="text-xs text-gray-500 font-light flex items-center gap-1 mt-0.5">
                            <FiBox className="w-3 h-3" /> {app.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 align-middle whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {formatDate(app.createdAt)}
                      </td>
                      <td className="px-4 py-4 align-middle whitespace-nowrap font-medium text-gray-900 dark:text-white">
                        {formatPrice(!isNaN(Number(app.totalPrice)) && app.totalPrice !== undefined && app.totalPrice !== null ? Number(app.totalPrice) : dynamicPrice)}
                      </td>
                      <td className="px-4 py-4 align-middle whitespace-nowrap">
                        <span className={`px-2.5 py-1.5 inline-flex items-center gap-1.5 text-xs font-semibold rounded-full ${getPaymentStatusColor(statusRaw)}`}>
                          {getPaymentStatusIcon(statusRaw)}
                          <span>{getPaymentStatusText(statusRaw)}</span>
                        </span>
                        {isPending && (
                          <div className="text-[10px] text-yellow-600 dark:text-yellow-400 mt-1.5 font-medium ml-1 flex items-center gap-1">
                            Aguardando confirmação do banco/PIX.
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 align-middle whitespace-nowrap text-right">
                        {isPending && (
                          <button
                            onClick={() => setSelectedAppForPayment(app)}
                            className="px-3 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium rounded hover:bg-black dark:hover:bg-gray-100 transition-colors shadow-sm"
                          >
                            Pagar Agora
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-900/30 rounded-none border border-gray-100 dark:border-gray-700/50 border-dashed">
            <FiBox className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <h4 className="text-lg font-medium text-gray-800 dark:text-gray-300 mb-1">Nenhuma fatura encontrada</h4>
            <p className="text-sm text-gray-500">Assim que você criar um app, a fatura aparecerá aqui.</p>
          </div>
        )}
      </div>
      {selectedAppForPayment && (
        <RetryPaymentModal 
          app={selectedAppForPayment} 
          onClose={() => setSelectedAppForPayment(null)}
          onSuccess={() => {
            setSelectedAppForPayment(null);
            alert("Pagamento processado com sucesso!");
            // Opção: forçar recarregamento ou atualização do estado local
            window.location.reload(); 
          }}
        />
      )}
    </div>
  );
};

export default FinancialPanel;